import { slugifySanityFaqQuestion, type SanityPageBuilderFaqQuestion, type SanityPageBuilderFaqSection, type SanityPageBuilderSection } from "@ouster/shared";
import { stripHtmlTags } from "~/features/app/utils";
import { cleanStegaForTextTransform } from "~/features/sanity/stega";
import { toHtml } from "~/features/sanity/rich-text/to-html";
const FAQ_SECTION_TYPE = "ouster.pageBuilder.section.faq";
export type FaqEntry = {
  /** `_key` of the owning page-builder section; empty when Sanity omitted one. */
  sectionKey: string;
  /** `_key` of the question inside that section. */
  questionKey: string;
  /** Page-unique anchor: the DOM id, the accordion value and the URL hash. */
  slug: string;
  question: string;
  /** Answer flattened to plain text, for JSON-LD. */
  answerText: string;
};

/**
 * Anchor for one question: authored slug wins, derived-from-question-text is the
 * fallback, positional id is the last resort (emoji-only or CJK questions
 * slugify to nothing).
 *
 * Stega is stripped first — without it the invisible payload survives into the
 * slug and ends up inside `id=""` and `location.hash` under preview.
 */
export function slugifyFaqQuestion(question: SanityPageBuilderFaqQuestion, index: number): string {
  const authored = question.questionSlug ? cleanStegaForTextTransform(question.questionSlug) : "";
  // Re-slugify rather than trusting the stored value: Studio validation only
  // guards new input, and older documents may predate that rule.
  const fromAuthored = authored ? slugifySanityFaqQuestion(authored) : "";
  if (fromAuthored) {
    return fromAuthored;
  }
  const derived = question.question ? slugifySanityFaqQuestion(cleanStegaForTextTransform(question.question)) : "";
  return derived || `faq-${index + 1}`;
}
function isFaqSection(section: SanityPageBuilderSection): section is SanityPageBuilderSection & {
  sectionContent: SanityPageBuilderFaqSection;
} {
  return section.sectionContent?._type === FAQ_SECTION_TYPE;
}

/**
 * Flatten every FAQ question on a page into one ordered, de-duplicated list.
 *
 * This is the single source of truth for both the anchors and the `FAQPage`
 * JSON-LD, so the two can never disagree. De-duplication is page-wide (not
 * per-section) because two sections would otherwise be able to claim the same
 * `#anchor`, making one of them unreachable by URL.
 *
 * Pure and deterministic, so the server and the client derive identical anchors
 * and hydration stays clean.
 */
export function collectFaqEntries(sections: SanityPageBuilderSection[]): FaqEntry[] {
  const entries: FaqEntry[] = [];
  const claimed = new Set<string>();
  for (const section of sections) {
    if (!isFaqSection(section)) {
      continue;
    }
    const questions = section.sectionContent.questions ?? [];
    questions.forEach((question, index) => {
      const base = slugifyFaqQuestion(question, index);
      let slug = base;
      let suffix = 2;
      while (claimed.has(slug)) {
        slug = `${base}-${suffix}`;
        suffix += 1;
      }
      claimed.add(slug);
      entries.push({
        sectionKey: section._key ?? "",
        questionKey: question._key ?? "",
        slug,
        question: question.question ? cleanStegaForTextTransform(question.question) : "",
        // `stripHtmlTags` also strips stega.
        answerText: stripHtmlTags(toHtml(question.answer) ?? "").trim()
      });
    });
  }
  return entries;
}

/** Index the entries by `${sectionKey}:${questionKey}` so a section can look up its own anchors. */
export function toFaqSlugMap(entries: FaqEntry[]): Map<string, string> {
  return new Map(entries.map(entry => [faqSlugMapKey(entry.sectionKey, entry.questionKey), entry.slug]));
}
export function faqSlugMapKey(sectionKey?: string | null, questionKey?: string | null): string {
  return `${sectionKey ?? ""}:${questionKey ?? ""}`;
}